import { getBackend } from "./backend/Backend2";
import * as t from "./backend/manufacturing2.generated.types";

import { fromShiftTag, getNumberOfShifts, shiftNumber, stringCompare } from "./Util";

export const MATERIAL_FORECAST_TYPES = {
    in: "in",
    out: "out",
    wip: "wip",
} as const;

export type MaterialForecastType = "in" | "out" | "wip";

export type IMaterialForecastWithType = t.IMaterialForecast & {
    material_type?: string;
};

export const MATERIAL_FORECAST_ONLY_ACCOUNTED = {
    yes: "yes",
    no: "no",
} as const;

export type MaterialForecastOnlyAccounted = "yes" | "no";

export const MATERIAL_FORECAST_SUBTYPES = {
    combined: "combined",
    consumed: "consumed",
    consumedByShift: "consumed_by_shift",
    produced: "produced",
    producedByShift: "produced_by_shift",
    receivedByShift: "received_by_shift",
    requiredByShift: "required_by_shift",
    required: "required",
} as const;

export type MaterialForecastSubtype =
    | "combined"
    | "consumed"
    | "consumed_by_shift"
    | "produced"
    | "produced_by_shift"
    | "received_by_shift"
    | "required_by_shift"
    | "required";

export const MATERIAL_FORECAST_LOCATIONS = {
    shopfloor: "shopfloor",
    plant: "plant",
} as const;

export type MaterialForecastLocation = "shopfloor" | "plant";

export type MaterialForecastShift = {
    date: string;
    day_of_week: number;
    shift: number;
};

export type MaterialForecastGroups = {
    materials_changing: IMaterialForecastWithType[];
    materials_nonchanging: IMaterialForecastWithType[];
    materials_zero: IMaterialForecastWithType[];
};

export type MaterialForecast = MaterialForecastGroups & {
    forecast_shifts: MaterialForecastShift[];
    report_id: string;
};

export type MaterialForecastParams = {
    group_uuid: string;
    type: MaterialForecastType;
    subtype: MaterialForecastSubtype;
    only_accounted_for_initial_stock: MaterialForecastOnlyAccounted;
    location: MaterialForecastLocation;
    forecast_horizon_days: number;
    line_uuid?: string;
    report_id?: string;
    order_external_ids?: string[];
    material_external_id?: string;
    include_unscheduled_stock?: boolean;
    material_types?: string[];
};

/**
 * Compare two material forecasts by title.
 * @param {IMaterialForecastWithType} a
 * @param {IMaterialForecastWithType} b
 * @returns {boolean}
 */
const compareMaterialForecastsByTitle = (a: IMaterialForecastWithType, b: IMaterialForecastWithType) =>
    stringCompare(a.material_title, b.material_title);

/**
 * Get shift headers from material forecasts and maximum number of days.
 * @param {IMaterialForecastWithType[]} material_forcasts - Material forecasts
 * @param {number} forecast_horizon_days - Forecast horizon days
 * @returns {MaterialForecastShift[]} Shift headers for given material forecasts and maximum number of days
 */
export const getMaterialForecastShifts = (
    material_forcasts: IMaterialForecastWithType[],
    forecast_horizon_days: number
): MaterialForecastShift[] => {
    const shifts: MaterialForecastShift[] = [];
    const total_shifts = getNumberOfShifts(forecast_horizon_days);
    const material_forecast = material_forcasts.find(x => x.forecasts.length > 0);
    if (material_forecast === undefined) {
        return shifts;
    }

    for (const item of material_forecast.forecasts) {
        const shift_date = fromShiftTag(item.shift_tag);
        const shift_number = shiftNumber(shift_date);
        if (shifts.length >= total_shifts) {
            break;
        }

        shifts.push({
            date: item.shift_date,
            day_of_week: shift_number.day_number,
            shift: shift_number.shift_day,
        });
    }

    return shifts;
};

/**
 * Categorize material forecasts into three groups based on stock changes:
 * - materials_changing: Material forecasts with stock level changes within `forecast_horizon_days` days
 * - materials_nonchanging: Material forecasts with stable positive stock levels for `forecast_horizon_days` days
 * - materials_zero: Material forecasts with stable zero stock levels for `forecast_horizon_days` days
 * @param {IMaterialForecastWithType[]} material_forcasts - Material forecasts
 * @param {number} forecast_horizon_days - Forecast horizon days
 * @returns {MaterialForecastGroups} Material forecast groups
 */
export const groupMaterialForecasts = (
    material_forcasts: IMaterialForecastWithType[],
    forecast_horizon_days: number
): MaterialForecastGroups => {
    const materials_changing: IMaterialForecastWithType[] = [];
    const materials_nonchanging: IMaterialForecastWithType[] = [];
    const materials_zero: IMaterialForecastWithType[] = [];
    const total_shifts = getNumberOfShifts(forecast_horizon_days);
    for (const material of material_forcasts) {
        const forecasts = material.forecasts.slice(0, total_shifts);
        if (forecasts.some(x => x.change)) {
            materials_changing.push(material);
        } else if (material.current_stock > 0) {
            materials_nonchanging.push(material);
        } else {
            materials_zero.push(material);
        }
    }

    // Sort materials by title
    materials_changing.sort(compareMaterialForecastsByTitle);
    materials_nonchanging.sort(compareMaterialForecastsByTitle);
    materials_zero.sort(compareMaterialForecastsByTitle);

    return {
        materials_changing,
        materials_nonchanging,
        materials_zero,
    };
};

export async function getMaterialForecast({
    group_uuid,
    type,
    subtype,
    only_accounted_for_initial_stock,
    location,
    forecast_horizon_days,
    line_uuid,
    report_id,
    order_external_ids,
    material_external_id,
    include_unscheduled_stock,
}: MaterialForecastParams): Promise<MaterialForecast> {
    const forecast_params: t.IGetMaterialForecastReq = {
        group_uuid,
        type,
        subtype,
        only_accounted_for_initial_stock,
        location,
        order_external_ids,
        material_external_id,
        include_unscheduled_stock,
    };

    if (line_uuid !== undefined) {
        forecast_params.line_uuid = line_uuid;
    }

    if (report_id !== undefined) {
        forecast_params.report_id = report_id;
    }

    const status = await getBackend().manufacturing.getMaterialForecast(forecast_params);
    const materials_all = await loadMaterialTypes(status);

    const { materials_changing, materials_nonchanging, materials_zero } = groupMaterialForecasts(
        materials_all,
        forecast_horizon_days
    );

    const forecast_shifts = getMaterialForecastShifts(materials_all, forecast_horizon_days);

    return {
        forecast_shifts,
        materials_changing,
        materials_nonchanging,
        materials_zero,
        report_id: status.report_id || "",
    };
}

const loadMaterialTypes = async (status: t.IGetMaterialForecastRes) => {
    const materials_all: IMaterialForecastWithType[] = status.items.filter(item => item.forecasts.length > 0);

    const external_ids = materials_all.map(item => item.material_external_id);

    const { data: materials_info } = await getBackend().manufacturing.searchMaterialsSimple({ external_ids });
    const materials_info_map = new Map(materials_info.map(m => [m.external_id, m]));

    for (const item of materials_all) {
        const material_info = materials_info_map.get(item.material_external_id);
        item.material_type = (material_info as t.IMaterialModel)?.tags.material_type;
    }

    return materials_all;
};
